import { Injectable } from '@angular/core';
import { Observable, delay, forkJoin, map, of, throwError } from 'rxjs';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { RequerimentoAdminItemDto, RequerimentoAdminQueryParams } from '../api/models';
import { EnumStatusResponse } from '../enum/enumHttpResponse';
import { requerimentoOnlineDetalheMock } from '../mocks/requerimento-beneficio/requerimento-online-solicitacoes.mock';
import {
  RequerimentoOnlineDetalhe,
  RequerimentoOnlineFiltros,
  RequerimentoOnlineOpcoesFiltro,
  RequerimentoOnlineSolicitacao,
} from '../models/requerimento-beneficio/requerimento-online-solicitacao.model';
import { extractIsoDate } from '../utils/date-format.util';
import { BeneficiosApiService } from './parametros/beneficios-api.service';
import { PlanosApiService } from './parametros/planos-api.service';
import { RequerimentoOnlineSolicitacaoApiService } from './requerimento-online-solicitacao-api.service';

/** Planos parametrizados cabem numa página: a API de Parametrização aceita até 100 por página. */
const PLANOS_POR_PAGINA = 100;

/** Latência simulada do detalhe mockado, para a tela exercitar o estado de carregamento. */
const MOCK_LATENCIA_MS = 300;

/**
 * Solicitações do Requerimento Online para a tela: traduz os filtros para a API e o retorno para o model. As chamadas
 * HTTP ficam no `RequerimentoOnlineSolicitacaoApiService`. O detalhe ainda vem do mock (PRE-346).
 */
@Injectable({
  providedIn: 'root',
})
export class RequerimentoOnlineSolicitacaoService {
  constructor(
    private readonly requerimentoApiService: RequerimentoOnlineSolicitacaoApiService,
    private readonly planosApiService: PlanosApiService,
    private readonly beneficiosApiService: BeneficiosApiService,
  ) {}

  /** A API filtra e devolve a lista inteira; ordenação e paginação ficam na tela. */
  listarSolicitacoes(filtros?: RequerimentoOnlineFiltros): Observable<RequerimentoOnlineSolicitacao[]> {
    return this.requerimentoApiService
      .listarRequerimentos(this.toQueryParams(filtros))
      .pipe(map((lista) => lista.map((dto) => this.mapSolicitacao(dto))));
  }

  /** Planos e benefícios ativos da Parametrização e os status da API de Requerimento, para os combos. */
  listarOpcoesFiltro(): Observable<RequerimentoOnlineOpcoesFiltro> {
    return forkJoin({
      planos: this.planosApiService
        .listarPlanosParams({ ativo: 'S', pagina: 1, tamanho_pagina: PLANOS_POR_PAGINA })
        .pipe(
          map((resposta) =>
            resposta.itens
              .filter((plano) => !!plano.cod_plano)
              .map((plano) => ({ value: plano.cod_plano!, label: plano.ds_plano ?? plano.cod_plano! })),
          ),
        ),
      beneficios: this.beneficiosApiService
        .listarCatalogo({ ativo: 'S' })
        .pipe(map((lista) => lista.map((beneficio) => ({ value: String(beneficio.id), label: beneficio.nome_exibicao })))),
      status: this.requerimentoApiService
        .listarStatus()
        .pipe(map((lista) => lista.map((status) => ({ value: status.tp_status, label: status.ds_status })))),
    });
  }

  /** Detalhe pelo `idSolicitacao` da lista; um id que não está no mock falha com 404, como uma API. */
  obterSolicitacao(idSolicitacao: number | string): Observable<RequerimentoOnlineDetalhe> {
    const detalhe = requerimentoOnlineDetalheMock(Number(idSolicitacao));
    const resposta$ = detalhe
      ? of(detalhe)
      : throwError(() => new ApiHttpError(EnumStatusResponse.NotFound, 'Solicitação não encontrada.'));

    return resposta$.pipe(delay(MOCK_LATENCIA_MS));
  }

  private toQueryParams(filtros: RequerimentoOnlineFiltros = {}): RequerimentoAdminQueryParams {
    return {
      nome: filtros.nome,
      matricula: filtros.matricula,
      cod_plano: filtros.codPlano,
      id_beneficio: filtros.idBeneficio,
      tp_status: filtros.tpStatus,
      dt_solicitacao_ini: filtros.periodoDe,
      dt_solicitacao_fim: filtros.periodoAte,
    };
  }

  private mapSolicitacao(dto: RequerimentoAdminItemDto): RequerimentoOnlineSolicitacao {
    return {
      idSolicitacao: dto.id,
      nomeParticipante: dto.nom_participante ?? '-',
      protocolo: dto.num_protocolo,
      matricula: dto.matricula,
      codPlano: dto.cod_plano,
      plano: dto.ds_plano ?? dto.cod_plano,
      idBeneficio: dto.id_beneficio,
      tipoBeneficio: dto.ds_beneficio ?? '-',
      dataSolicitacao: dto.dt_solicitacao ? extractIsoDate(dto.dt_solicitacao) : null,
      tpStatus: dto.tp_status,
      status: dto.ds_status,
    };
  }
}
